import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from '@/routing/navigation'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { experimentsApi } from '@/lib/api'
import {
  Search,
  LayoutDashboard,
  FlaskConical,
  Users,
  Plus,
  ArrowRight,
  Clock,
  MessageSquare,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface CommandItem {
  id: string
  label: string
  description?: string
  icon: React.ReactNode
  action: () => void
  keywords?: string[]
  group: 'navigation' | 'actions' | 'recent'
}

interface Experiment {
  id: string
  name: string
  status: string
  createdAt?: string
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const router = useRouter()
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [loadingExperiments, setLoadingExperiments] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const navigate = useCallback((path: string) => {
    onOpenChange(false)
    router.push(path)
  }, [router, onOpenChange])

  // Fetch recent experiments when palette opens (paginated for performance)
  useEffect(() => {
    if (open) {
      setLoadingExperiments(true)
      experimentsApi.listPaginated(1, 50)
        .then((response) => {
          // Store recent experiments for searching (50 most recent)
          setExperiments(response.experiments)
        })
        .catch((err) => {
          console.error('Failed to fetch experiments:', err)
          setExperiments([])
        })
        .finally(() => setLoadingExperiments(false))
    }
  }, [open])

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return t('commandPalette.statusCompleted')
      case 'running': return t('commandPalette.statusRunning')
      case 'pending': return t('commandPalette.statusPending')
      case 'failed': return t('commandPalette.statusFailed')
      default: return status
    }
  }

  const staticCommands: Array<CommandItem> = useMemo(() => [
    {
      id: 'nav-dashboard',
      label: t('commandPalette.dashboard'),
      description: t('commandPalette.goDashboard'),
      icon: <LayoutDashboard className="h-4 w-4" />,
      action: () => navigate('/'),
      keywords: ['home', 'main', 'overview'],
      group: 'navigation',
    },
    {
      id: 'nav-questions',
      label: t('commandPalette.questions'),
      description: t('commandPalette.askCustomers'),
      icon: <FlaskConical className="h-4 w-4" />,
      action: () => navigate('/tests/new'),
      keywords: ['ask', 'survey', 'create', 'new'],
      group: 'navigation',
    },
    {
      id: 'nav-answers',
      label: t('commandPalette.answers'),
      description: t('commandPalette.viewAnswers'),
      icon: <MessageSquare className="h-4 w-4" />,
      action: () => navigate('/tests'),
      keywords: ['results', 'reports', 'responses', 'tests'],
      group: 'navigation',
    },
    {
      id: 'nav-populations',
      label: t('commandPalette.populations'),
      description: t('commandPalette.viewPopulations'),
      icon: <Users className="h-4 w-4" />,
      action: () => navigate('/audiences'),
      keywords: ['personas', 'respondents', 'audiences'],
      group: 'navigation',
    },
    {
      id: 'nav-featured-twins',
      label: t('commandPalette.featuredTwins'),
      description: t('commandPalette.manageFeatured'),
      icon: <Sparkles className="h-4 w-4" />,
      action: () => navigate('/audiences'),
      keywords: ['twins', 'persona', 'featured'],
      group: 'navigation',
    },
    {
      id: 'action-new-question',
      label: t('commandPalette.askQuestion'),
      description: t('commandPalette.createQuestion'),
      icon: <Plus className="h-4 w-4" />,
      action: () => navigate('/tests/new'),
      keywords: ['create', 'add', 'start', 'new'],
      group: 'actions',
    },
    {
      id: 'action-new-population',
      label: t('commandPalette.newPopulation'),
      description: t('commandPalette.createPopulation'),
      icon: <Plus className="h-4 w-4" />,
      action: () => navigate('/audiences/new'),
      keywords: ['create', 'add', 'upload'],
      group: 'actions',
    },
  ], [navigate, t])

  // Build experiment commands from ALL experiments
  const allExperimentCommands: CommandItem[] = experiments.map(exp => ({
    id: `exp-${exp.id}`,
    label: exp.name || t('commandPalette.untitledExperiment'),
    description: getStatusLabel(exp.status),
    icon: <Clock className="h-4 w-4" />,
    action: () => navigate(`/tests/${exp.id}/results`),
    keywords: ['experiment', 'result', 'report'],
    group: 'recent' as const,
  }))

  const allCommands = [...staticCommands, ...allExperimentCommands]

  // Filter commands based on query
  const filteredCommands = allCommands.filter((cmd) => {
    if (!query) return true
    const searchTerms = query.toLowerCase().split(' ')
    const searchableText = [
      cmd.label,
      cmd.description,
      ...(cmd.keywords || []),
    ].join(' ').toLowerCase()

    return searchTerms.every(term => searchableText.includes(term))
  })

  // Group filtered commands
  // When no query: show only 5 recent experiments
  // When searching: show all matching experiments
  const filteredExperiments = filteredCommands.filter(c => c.group === 'recent')
  const groupedCommands = {
    navigation: filteredCommands.filter(c => c.group === 'navigation'),
    actions: filteredCommands.filter(c => c.group === 'actions'),
    recent: query ? filteredExperiments : filteredExperiments.slice(0, 5),
  }

  const flatFilteredCommands = useMemo(
    () => [
      ...groupedCommands.navigation,
      ...groupedCommands.actions,
      ...groupedCommands.recent,
    ],
    [groupedCommands.actions, groupedCommands.navigation, groupedCommands.recent],
  )

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      // Focus input after dialog animation
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(i =>
          i < flatFilteredCommands.length - 1 ? i + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(i =>
          i > 0 ? i - 1 : flatFilteredCommands.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (flatFilteredCommands[selectedIndex]) {
          flatFilteredCommands[selectedIndex].action()
        }
        break
      case 'Escape':
        e.preventDefault()
        onOpenChange(false)
        break
    }
  }, [flatFilteredCommands, selectedIndex, onOpenChange])

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    selectedElement?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  const renderGroup = (title: string, items: CommandItem[], startIndex: number) => {
    if (items.length === 0) return null

    return (
      <div className="mb-2">
        <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {title}
        </div>
        {items.map((cmd, idx) => {
          const globalIndex = startIndex + idx
          const isSelected = selectedIndex === globalIndex

          return (
            <button
              key={cmd.id}
              data-index={globalIndex}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-md transition-colors",
                isSelected
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-muted"
              )}
              onClick={() => cmd.action()}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
            >
              <span className={cn(
                "shrink-0",
                isSelected ? "text-accent-foreground" : "text-muted-foreground"
              )}>
                {cmd.icon}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{cmd.label}</div>
                {cmd.description && (
                  <div className={cn(
                    "text-xs truncate",
                    isSelected ? "text-accent-foreground" : "text-muted-foreground"
                  )}>
                    {cmd.description}
                  </div>
                )}
              </div>
              <ArrowRight className={cn(
                "h-3 w-3 shrink-0 opacity-0 transition-opacity",
                isSelected && "opacity-100"
              )} />
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Hide default X button to prevent overlap with ESC chip */}
      <DialogContent className="p-0 gap-0 max-w-lg overflow-hidden [&>button]:hidden">
        <DialogTitle className="sr-only">{t('commandPalette.title')}</DialogTitle>
        {/* Search Input */}
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('commandPalette.placeholder')}
            className="h-12 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60"
          />
          <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-[350px] overflow-y-auto p-2"
        >
          {flatFilteredCommands.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t('commandPalette.noResults', { query })}
            </div>
          ) : (
            <>
              {renderGroup(t('commandPalette.groupNavigation'), groupedCommands.navigation, 0)}
              {renderGroup(t('commandPalette.groupActions'), groupedCommands.actions, groupedCommands.navigation.length)}
              {renderGroup(
                query ? t('commandPalette.groupExperiments') : t('commandPalette.groupRecent'),
                groupedCommands.recent,
                groupedCommands.navigation.length + groupedCommands.actions.length
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <kbd className="inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px]">↑↓</kbd>
            <span>{t('commandPalette.navigate')}</span>
          </div>
          <div className="flex items-center gap-2">
            <kbd className="inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px]">↵</kbd>
            <span>{t('commandPalette.select')}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Hook for global keyboard shortcut
export function useCommandPalette() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return { open, setOpen }
}
